// @ts-nocheck
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import CartManager from './cart-manager.js';

const DYNAMIC_UPSELL_SELECTOR = '[upsell-carousel][data-dynamic-recs="true"]';
const requestTokenByRoot = new WeakMap();
const productCache = new Map();
const productPromiseCache = new Map();
const DEBUG_UPSELL = false;

function debugLog(...args) {
  if (!DEBUG_UPSELL) return;
  console.log('[UpsellDebug]', ...args);
}

function getDynamicUpsellRoots() {
  const roots = [...document.querySelectorAll(DYNAMIC_UPSELL_SELECTOR)];
  debugLog('Found dynamic upsell roots:', roots.length);
  return roots;
}

function toIdentifier(item) {
  if (!item) return '';
  const candidate = item.sku || item.product_id;
  if (candidate === null || candidate === undefined) return '';
  return String(candidate).trim();
}

function getCartIdentifiers(cart) {
  const seen = new Set();
  const identifiers = [];
  (cart?.items || []).forEach((item) => {
    const value = toIdentifier(item);
    if (!value || seen.has(value)) return;
    seen.add(value);
    identifiers.push(value);
  });
  return identifiers;
}

function readLastViewedIdentifiers() {
  const keys = ['searchspring:lastViewed', 'ss_lastViewed', 'lastViewed'];
  for (const key of keys) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const values = Array.isArray(parsed) ? parsed : String(raw).split(',');
      const cleaned = values
        .map((item) => (typeof item === 'string' ? item : item?.sku || item?.id || item?.product_id))
        .filter(Boolean)
        .map((item) => String(item).trim())
        .filter(Boolean);
      if (cleaned.length) return cleaned;
    } catch (error) {
      const fallback = window.localStorage
        .getItem(key)
        ?.split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      if (fallback?.length) return fallback;
    }
  }
  return [];
}

async function fetchCart() {
  const response = await fetch(`${window.Shopify.routes.root}cart.js`, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  const cart = await response.json();
  debugLog('Cart fetched', {
    itemCount: cart?.item_count,
    identifiers: getCartIdentifiers(cart),
    rawItems: cart?.items?.map((item) => ({
      sku: item?.sku,
      product_id: item?.product_id,
      variant_id: item?.variant_id,
      title: item?.title,
    })),
  });
  return cart;
}

function buildRecommendationUrl(root, cartIdentifiers) {
  const siteId = root.dataset.siteId || 'ccwfx6';
  const tag = root.dataset.recommendationTag || 'view-cart';
  const limit = root.dataset.recommendationLimit || '';
  const shopperId = root.dataset.shopperId || '';
  const url = new URL(`https://${siteId}.a.searchspring.io/boost/${siteId}/recommend`);
  const params = url.searchParams;

  params.set('tags', tag);
  params.set('cart', cartIdentifiers.join(','));
  params.set('blockedItems', cartIdentifiers.join(','));
  if (limit) params.set('limits', String(limit));
  if (shopperId) params.set('shopper', shopperId);

  const lastViewed = readLastViewedIdentifiers();
  if (lastViewed.length) {
    params.set('lastViewed', lastViewed.join(','));
  }

  const finalUrl = url.toString();
  debugLog('Recommendation URL built', {
    siteId,
    tag,
    limit,
    shopperId,
    cartIdentifiers,
    finalUrl,
  });
  return finalUrl;
}

function escapeHtml(value = '') {
  const html = String(value);
  return html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatShopifyCents(cents) {
  const amount = Number(cents);
  if (!Number.isFinite(amount)) return '';
  const formatString = window.Shopify?.format?.money || window.theme?.moneyFormat || '${{amount}}';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const match = formatString.match(placeholderRegex);
  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    const sNumber = (number / 100.0).toFixed(precision);
    const parts = sNumber.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsStr = parts[1] ? (decimal + parts[1]) : '';
    return dollars + centsStr;
  }
  let value = '';
  if (match) {
    switch (match[1]) {
      case 'amount': value = formatWithDelimiters(amount, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(amount, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(amount, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(amount, 0, '.', ','); break;
      default: value = formatWithDelimiters(amount, 2);
    }
  }
  return formatString.replace(placeholderRegex, value);
}

function formatPrice(dollars) {
  return formatShopifyCents(Math.round(Number(dollars) * 100));
}

function buildResponsiveSrcset(url) {
  if (!url) return '';
  const widths = [200, 400];
  return widths.map((width) => `${url}${url.includes('?') ? '&' : '?'}width=${width} ${width}w`).join(', ');
}

function getProductHandle(productUrl) {
  if (!productUrl || productUrl === '#') return '';
  try {
    const url = new URL(productUrl, window.location.origin);
    const parts = url.pathname.split('/').filter(Boolean);
    const productsIndex = parts.indexOf('products');
    if (productsIndex === -1) return '';
    return parts[productsIndex + 1] || '';
  } catch (error) {
    return '';
  }
}

async function fetchShopifyProduct(handle) {
  if (!handle) return null;

  if (productCache.has(handle)) {
    return productCache.get(handle);
  }

  if (productPromiseCache.has(handle)) {
    return productPromiseCache.get(handle);
  }

  const promise = fetch(`${window.Shopify.routes.root}products/${handle}.js`, { cache: 'force-cache' })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to load product ${handle}: ${response.status}`);
      }
      return response.json();
    })
    .then((product) => {
      productCache.set(handle, product);
      return product;
    })
    .finally(() => {
      productPromiseCache.delete(handle);
    });

  productPromiseCache.set(handle, promise);
  return promise;
}

function getVariantLabel(variant) {
  if (variant?.public_title && variant.public_title !== 'Default Title') return variant.public_title;
  if (Array.isArray(variant?.options) && variant.options.length) return variant.options.join(' / ');
  return variant?.title || `Variant ${variant?.id || ''}`.trim();
}

function isVariantPurchasable(variant) {
  if (!variant) return false;
  if (variant.available === false) return false;
  if (typeof variant.inventory_quantity === 'number' && variant.inventory_quantity <= 0 && !variant.available) return false;
  return true;
}

function getVariantImage(variant, product, currentImage) {
  return (
    variant?.featured_image?.src ||
    product?.featured_image ||
    (Array.isArray(product?.images) ? product.images[0] : '') ||
    currentImage ||
    ''
  );
}

function applyVariantToTile(tile, variant, product) {
  if (!tile || !variant) return;

  const image = tile.querySelector('.upsellTile__img');
  const priceCurrent = tile.querySelector('.price-value__current');
  const priceCompare = tile.querySelector('.price-value__compare');
  const button = tile.querySelector('.upsellTile__ctaButton');

  const nextImageSrc = getVariantImage(variant, product, image?.getAttribute('src') || '');
  if (image && nextImageSrc) {
    image.setAttribute('src', nextImageSrc);
    image.setAttribute('srcset', buildResponsiveSrcset(nextImageSrc));
    image.setAttribute('sizes', '(max-width: 768px) 100vw, 50vw');
    image.setAttribute('alt', product?.title || image.getAttribute('alt') || 'Upsell product');
  }

  if (priceCurrent) {
    priceCurrent.textContent = formatShopifyCents(variant.price);
  }
  if (priceCompare) {
    const hasCompare = Number(variant?.compare_at_price) > Number(variant?.price);
    priceCompare.textContent = hasCompare ? formatShopifyCents(variant.compare_at_price) : '';
  }

  if (button) {
    button.setAttribute('add-to-cart', String(variant.id));
    if (isVariantPurchasable(variant)) {
      button.removeAttribute('disabled');
      button.textContent = '+ Add';
    } else {
      button.setAttribute('disabled', 'disabled');
      button.textContent = 'Out of Stock';
    }
  }
}

async function hydrateTileVariants(tile, requestToken, root) {
  if (!tile) return;

  const handle = tile.dataset.productHandle;
  if (!handle) return;

  const button = tile.querySelector('.upsellTile__ctaButton');
  const optionsWrap = tile.querySelector('.upsellTile__options');
  const select = tile.querySelector('.upsellTile__optionsInput');
  const price = tile.querySelector('.upsellTile__productValue');

  try {
    const product = await fetchShopifyProduct(handle);
    if (requestTokenByRoot.get(root) !== requestToken) return;
    if (!product || !Array.isArray(product.variants) || !select || !button) return;

    const allVariants = product.variants;
    const availableVariants = allVariants.filter((variant) => isVariantPurchasable(variant));
    const selectedVariant = availableVariants[0] || null;
    const productHasAnyStock = availableVariants.length > 0;

    select.innerHTML = allVariants
      .map((variant) => {
        const label = getVariantLabel(variant);
        const purchasable = isVariantPurchasable(variant);
        const disabled = purchasable ? '' : 'disabled';
        const selected = selectedVariant && variant.id === selectedVariant.id ? 'selected' : '';
        const displayLabel = purchasable ? label : `${label} - Out of Stock`;
        return `<option value="${escapeHtml(variant.id)}" ${disabled} ${selected} data-handle="${escapeHtml(handle)}">${escapeHtml(displayLabel)}</option>`;
      })
      .join('');

    if (allVariants.length > 1 && optionsWrap) {
      optionsWrap.hidden = false;
      tile.querySelector('.upsellTile__content')?.classList.remove('upsellTile__content--noVariants');
    }

    tile._upsellProduct = product;
    tile.dataset.productAvailable = productHasAnyStock ? 'true' : 'false';
    if (selectedVariant && productHasAnyStock) {
      applyVariantToTile(tile, selectedVariant, product);
    } else {
      tile.classList.add('upsellTile--outOfStock');
      button.setAttribute('disabled', 'disabled');
      button.removeAttribute('add-to-cart');
      button.textContent = 'Out of Stock';
    }

    if (price && !price.textContent.trim() && selectedVariant) {
      const currentPrice = tile.querySelector('.price-value__current');
      if (currentPrice) {
        currentPrice.textContent = formatShopifyCents(selectedVariant.price);
      }
    }
  } catch (error) {
    debugLog('Failed to hydrate variants for handle', handle, error);
  }
}

function hydrateRenderedTiles(root, requestToken) {
  const tiles = [...root.querySelectorAll('.upsellCarousel__slide--dynamic .upsellTile')];
  return Promise.all(tiles.map((tile) => hydrateTileVariants(tile, requestToken, root)));
}

function renderRecommendations(root, results) {
  const wrapper = root.querySelector('.swiper-wrapper');
  if (!wrapper) return;

  wrapper.querySelectorAll('.upsellCarousel__slide--dynamic').forEach((node) => node.remove());
  const upsellEntry = wrapper.querySelector('[upsell-entry]');
  if (upsellEntry) upsellEntry.hidden = true;

  const fragment = document.createDocumentFragment();

  results.forEach((result) => {
    const core = result?.mappings?.core || {};
    const attributes = result?.attributes || {};
    const name = core.name || '';
    const price = formatPrice(core.price);
    const imageUrl = core.imageUrl || core.thumbnailImageUrl || '';
    const productUrl = core.url || '#';
    const productId = result?.id || '';
    const productType = core.type || attributes.type || attributes.product_type || '';
    const productHandle = getProductHandle(productUrl);
    if (!productHandle) return;

    const slide = document.createElement('div');
    slide.className = 'swiper-slide upsellCarousel__slide upsellCarousel__slide--dynamic';
    slide.setAttribute('product-id', escapeHtml(productId));
    slide.setAttribute('product-type', escapeHtml(productType));
    slide.innerHTML = `
      <div class="upsellTile" data-product-id="${escapeHtml(productId)}" data-product-handle="${escapeHtml(productHandle)}">
        <a href="${escapeHtml(productUrl)}" class="upsellTile__imageContainer" style="display: block;">
          <img
            class="inlineImg lazyload upsellTile__img"
            src="${escapeHtml(imageUrl)}"
            srcset="${escapeHtml(buildResponsiveSrcset(imageUrl))}"
            sizes="(max-width: 768px) 100vw, 50vw"
            style="--focal-x: 50%; --focal-y: 50%;"
            alt="${escapeHtml(name)}"
            loading="lazy" width="200" height="200"
            fetchpriority="low"
          >
        </a>
        <div class="upsellTile__content upsellTile__content--noVariants">
          <div class="upsellTile__productDetails">
            <a href="${escapeHtml(productUrl)}" class="upsellTile__productTitle checking" title="${escapeHtml(name)}" style="display: inline;">${escapeHtml(name)}</a>
            <div class="upsellTile__metaRow">
              <span class="price-value upsellTile__productValue">
                <span class="price-value__current">${escapeHtml(price)}</span>
                <s title="Regular price" class="price-value__compare"></s>
              </span>
              <div class="upsellTile__options" hidden>
                <div class="upsellTile__optionsSelector">
                  <select class="upsellTile__optionsInput"></select>
                  <svg class="symbol-chevron-down" aria-hidden="true">
                    <use xlink:href="#icon-chevron-down"></use>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div class="upsellTile__cta">
            <button
              type="button"
              class="upsellTile__ctaButton bttn bttn--secondary"
              add-to-cart-swatch="false"
              add-to-cart=""
              disabled
            >
              + Add
            </button>
          </div>
        </div>
      </div>
    `;
    fragment.appendChild(slide);
  });

  wrapper.appendChild(fragment);
  debugLog('Rendered recommendation slides', {
    renderedCount: results.length,
    rootClassList: root.className,
  });
}

function updateVisibility(root, hasResults, hasCartItems) {
  if (hasResults && hasCartItems) {
    root.classList.remove('hide');
  } else {
    root.classList.add('hide');
  }
  debugLog('Visibility updated', {
    hasResults,
    hasCartItems,
    isHidden: root.classList.contains('hide'),
  });
}

function updateSwiper(root) {
  const carousel = root.querySelector('.upsellCarousel__carousel');
  if (carousel?.swiper) {
    carousel.swiper.update();
  }
}

async function fetchRecommendations(url) {
  debugLog('Fetching recommendations', url);
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
  const payload = await response.json();
  debugLog('Raw recommendation payload', payload);
  return payload;
}

function extractResults(payload) {
  if (!payload) return [];

  if (Array.isArray(payload)) {
    // Common Searchspring format: [{ profile: {...}, results: [...] }]
    if (payload.length && Array.isArray(payload[0]?.results)) {
      const merged = payload.flatMap((entry) => (Array.isArray(entry?.results) ? entry.results : []));
      debugLog('Extracted results from array payload', { count: merged.length });
      return merged;
    }
    // Fallback: payload itself might be a results array
    const direct = payload.filter((entry) => entry?.mappings?.core);
    debugLog('Extracted results from direct array payload', { count: direct.length });
    return direct;
  }

  if (Array.isArray(payload.results)) {
    debugLog('Extracted results from payload.results', { count: payload.results.length });
    return payload.results;
  }

  if (Array.isArray(payload?.data?.results)) {
    debugLog('Extracted results from payload.data.results', { count: payload.data.results.length });
    return payload.data.results;
  }

  debugLog('No results array found in payload');
  return [];
}

async function refreshRoot(root, cart) {
  const cartIdentifiers = getCartIdentifiers(cart);
  debugLog('Refreshing root', {
    cartIdentifiers,
    cartItemCount: cart?.item_count,
  });
  const requestToken = Symbol('upsell-request');
  requestTokenByRoot.set(root, requestToken);

  if (!cartIdentifiers.length) {
    renderRecommendations(root, []);
    updateVisibility(root, false, false);
    updateSwiper(root);
    return;
  }

  try {
    const url = buildRecommendationUrl(root, cartIdentifiers);
    const data = await fetchRecommendations(url);

    if (requestTokenByRoot.get(root) !== requestToken) return;

    const results = extractResults(data);
    debugLog('Final extracted results count', results.length);
    renderRecommendations(root, results);
    await hydrateRenderedTiles(root, requestToken);
    root.querySelectorAll('.upsellTile[data-product-available="false"]').forEach((tile) => {
      tile.closest('.upsellCarousel__slide--dynamic')?.remove();
    });
    const availableSlides = root.querySelectorAll('.upsellCarousel__slide--dynamic').length;
    updateVisibility(root, availableSlides > 0, cart.item_count > 0);
    updateSwiper(root);
  } catch (error) {
    if (requestTokenByRoot.get(root) !== requestToken) return;
    renderRecommendations(root, []);
    updateVisibility(root, false, cart.item_count > 0);
    updateSwiper(root);
    console.error('Failed to fetch Searchspring cart recommendations:', error);
  }
}

function resolveCartFromEvent(event) {
  return event?.detail?.resource?.cart ?? null;
}

async function resolveCartForRefresh(event) {
  const eventCart = resolveCartFromEvent(event);
  if (eventCart) return eventCart;

  const managerCart = CartManager.getInstance().getState();
  if (managerCart) return managerCart;

  const bootstrapCart = window.getBootstrapCart?.();
  if (bootstrapCart) return bootstrapCart;

  return fetchCart();
}

async function refreshDynamicUpsells(event) {
  const roots = getDynamicUpsellRoots();
  if (!roots.length) return;

  try {
    const cart = await resolveCartForRefresh(event);
    await Promise.all(roots.map((root) => refreshRoot(root, cart)));
  } catch (error) {
    console.error('Failed to refresh dynamic upsells:', error);
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  refreshDynamicUpsells(event);
});

document.addEventListener('DOMContentLoaded', () => {
  refreshDynamicUpsells();
  document.addEventListener('change', (e) => {
    if (e.target.classList.contains('upsellTile__optionsInput')) {
      const select = e.target;
      const tile = select.closest('.upsellTile');
      const button = tile?.querySelector('.upsellTile__ctaButton');
      const selectedVariantId = select.value;
      
      if (button && selectedVariantId) {
        button.setAttribute('add-to-cart', selectedVariantId);
      }

      const product = tile?._upsellProduct;
      if (product && Array.isArray(product.variants)) {
        const nextVariant = product.variants.find((variant) => String(variant.id) === String(selectedVariantId));
        if (nextVariant) {
          applyVariantToTile(tile, nextVariant, product);
        }
      }
    }
  });
// Upsell ADD TO CART HANDLER
document.addEventListener('click', async (e) => {
  const button = e.target.closest('.upsellTile__ctaButton');
  if (!button) return;

  if (!button.hasAttribute('add-to-cart')) {
    return;
  }

  e.preventDefault();
  if (button.classList.contains('is-loading')) return;

  const tile = button.closest('.upsellTile');
  const select = tile?.querySelector('.upsellTile__optionsInput');
  const variantId = select ? select.value : button.getAttribute('add-to-cart');
  const productId = tile?.dataset.productId;
  if (!variantId) return;

  const product = tile?._upsellProduct;
  if (product && Array.isArray(product.variants)) {
    const selectedVariant = product.variants.find((variant) => String(variant.id) === String(variantId));
    if (!isVariantPurchasable(selectedVariant)) {
      button.setAttribute('disabled', 'disabled');
      button.textContent = 'Out of Stock';
      button.removeAttribute('add-to-cart');
      return;
    }
  }

  /* Loading state */
  const originalText = button.textContent.trim();
  button.classList.add('is-loading');
  button.textContent = 'Adding…';
  button.setAttribute('disabled', 'disabled');

  try {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set();
    cartItemsComponents.forEach((item) => {
      if (item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const result = await response.json();

    if (result.errors) {
      console.error('Upsell add-to-cart error:', result.errors);
      return;
    }

    const cart = await CartManager.getInstance().get();
    const displayCount = window.getCartItemCount?.(cart) ?? cart?.item_count ?? 0;

    document.dispatchEvent(
      new CartUpdateEvent({ cart }, null, {
        itemCount: displayCount,
        sections: result.sections,
        source: 'upsell-carousel',
      })
    );

    /* Remove the added upsell tile */
    if (tile && productId) {
      tile.classList.add('is-removing');
      setTimeout(() => {
        tile.remove();
      }, 200);
    }

    /* Ensure cart drawer is open */
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
    refreshDynamicUpsells();

  } catch (error) {
    console.error('Upsell ATC failed:', error);
  } finally {
    if (button.isConnected) {
      button.classList.remove('is-loading');
      button.textContent = originalText;
      button.removeAttribute('disabled');
    }
  }
});
});